import React, { useState, useEffect } from 'react';
import moment from 'moment';
import './loyalty.css';
import getLoyaltyDashboard from '../../../api/graphql/queries/getLoyaltyDashboard';
import getLoyaltyPrizes from '../../../api/graphql/queries/getLoyaltyPrizes';
import getLoyaltyActivity from '../../../api/graphql/queries/getLoyaltyActivity';
import getLoyaltyRanking from '../../../api/graphql/queries/getLoyaltyRanking';
import getLoyaltyUserDetail from '../../../api/graphql/queries/getLoyaltyUserDetail';
import { buildPrizeSlugOptions } from '../../../utils/loyaltyPrizes';
import LoadIcon from '../../LoadIcon';
import LoyaltyKpis from './LoyaltyKpis';
import LoyaltyActivity from './LoyaltyActivity';
import LoyaltyToolbar from './LoyaltyToolbar';
import LoyaltyRankingTable from './LoyaltyRankingTable';
import LoyaltyUserModal from './LoyaltyUserModal';
import LoyaltyPrizesView from './LoyaltyPrizesView';

const PAGE_SIZE = 25;
const SECTION_PAGE = 10; // recent-activity loads 10 at a time
const EXPORT_MAX_ROWS = 50000;

// CSV columns for the ranking export. Deliberately excludes prize `code`/`link` — raw prize
// codes must never appear in a bulk export (detail-view only, see LoyaltyUserModal).
const EXPORT_COLUMNS = [
  { field: 'email', headerName: 'email' },
  { field: 'name', headerName: 'name' },
  { field: 'totalPoints', headerName: 'totalPoints' },
  { field: 'achievementsCount', headerName: 'achievementsCount' },
  { field: 'prizesCount', headerName: 'prizesCount' },
  { field: 'prizesClaimedCount', headerName: 'prizesClaimedCount' },
  { field: 'lastActivityAt', headerName: 'lastActivityAt' },
];

// Wrap a value in quotes (doubling any internal quotes) whenever it contains a comma, quote
// or newline, so fan names with commas can't corrupt the CSV column layout.
const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const formatExportValue = (field, value) => {
  if (field === 'lastActivityAt') {
    return value ? moment(Number(value) || value).format('YYYY-MM-DD HH:mm') : '';
  }
  return value === null || value === undefined ? '' : value;
};

// Same approach as UsersContainer/SubscriptionsContainer's makeCsv: one header row plus one
// row per item, columns joined with commas.
const makeLoyaltyCsv = (items) => {
  const rows = new Array(items.length + 1);
  rows[0] = EXPORT_COLUMNS.map(column => column.headerName).join(',');
  for (let rowNum = 0; rowNum < items.length; rowNum++) {
    rows[rowNum + 1] = EXPORT_COLUMNS
      .map(column => escapeCsvValue(formatExportValue(column.field, items[rowNum][column.field])))
      .join(',');
  }
  return rows.join('\n');
};

const DEFAULT_FILTERS = {
  email: '',
  type: '',
  slug: '',
  claimed: '',
  startDate: '',
  finishDate: '',
};

const LoyaltyContainer = () => {
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Which sub-view of the Loyalty tab is showing: the fan leaderboard (existing toolbar +
  // ranking table + panels) or the Prizes ledger (Task 6).
  const [view, setView] = useState('leaderboard');

  // Ranking table state. filters/page/sorting are kept in a single object and updated
  // atomically (one setState per user action) — this app is on React 16, which does not
  // auto-batch state updates triggered from outside a synthetic event handler (e.g. the
  // toolbar's debounce timer), so separate setState calls here would cause the fetch
  // effect below to fire twice per action (once with stale page, once corrected).
  const [ranking, setRanking] = useState([]);
  const [rankingHasMore, setRankingHasMore] = useState(false);
  const [rankingAsOf, setRankingAsOf] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [downloadingInProgress, setDownloadingInProgress] = useState(false);
  const [queryState, setQueryState] = useState({
    filters: DEFAULT_FILTERS,
    page: 0,
    sorting: ['totalPoints', -1],
  });
  const { filters, page, sorting } = queryState;

  // Full prize catalogue (all 82 on production) — feeds the toolbar's Prize
  // dropdown so a prize with zero wins can still be selected/shown (see
  // buildPrizeSlugOptions). Fetched once, not paginated: unlike the removed
  // per-prize breakdown panel, the dropdown needs every prize up front.
  const [prizes, setPrizes] = useState([]);

  // Recent-activity feed (its own lightweight, "load more" query so the
  // dashboard payload stays small).
  const [activityItems, setActivityItems] = useState([]);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);

  // Fan-detail modal state.
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedRank, setSelectedRank] = useState('—');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = await getLoyaltyDashboard();
      if (mounted) {
        setDashboard(data);
        setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const isAuth = !!(dashboard && dashboard.isAuth);

  // Initial load, once we know the admin is authed: the full prize catalogue
  // (one shot) and the first page of the recent-activity feed (paginated).
  useEffect(() => {
    if (!isAuth) return undefined;

    let cancelled = false;
    (async () => {
      const prizesRes = await getLoyaltyPrizes();
      if (!cancelled) {
        setPrizes((prizesRes && prizesRes.items) || []);
      }

      setActivityLoading(true);
      const activityRes = await getLoyaltyActivity({ from: 0, to: SECTION_PAGE });
      if (!cancelled) {
        setActivityItems((activityRes && activityRes.items) || []);
        setActivityHasMore(!!(activityRes && activityRes.hasMore));
        setActivityLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuth]);

  // Fetch the ranking page whenever filters/page/sorting change. getTotal is false:
  // an exact count would need a second full-collection group scan, and the "Fans w/
  // points" KPI already shows the population — pagination uses has-more instead.
  useEffect(() => {
    if (!isAuth) return undefined;

    let cancelled = false;
    setRankingLoading(true);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE;

    getLoyaltyRanking({
      ...filters,
      from,
      to,
      sortingField: sorting[0],
      sortingType: sorting[1],
      getTotal: false,
    }).then((result) => {
      if (cancelled) return;
      const items = (result && result.isAuth && result.items) || [];
      setRanking(items);
      setRankingHasMore(items.length === PAGE_SIZE);
      setRankingAsOf(result && result.asOf ? result.asOf : null);
      setRankingLoading(false);
    });

    return () => { cancelled = true; };
  }, [isAuth, queryState]);

  // Fetch the fan-detail payload whenever a fan is selected (ranking row or activity feed).
  useEffect(() => {
    if (!selectedEmail) return undefined;

    let cancelled = false;
    setDetailLoading(true);
    setDetail(null);

    getLoyaltyUserDetail({ email: selectedEmail }).then((result) => {
      if (cancelled) return;
      setDetail(result);
      setDetailLoading(false);
    });

    return () => { cancelled = true; };
  }, [selectedEmail]);

  const handleFilterChange = (partial) => {
    setQueryState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...partial },
      page: 0,
    }));
  };

  const handleSort = (field) => {
    setQueryState((prev) => {
      const [prevField, prevDirection] = prev.sorting;
      let nextDirection = -1;
      if (prevField === field) {
        nextDirection = prevDirection === 1 ? -1 : 1;
      }
      return { ...prev, sorting: [field, nextDirection], page: 0 };
    });
  };

  // Triggered from the Prizes drawer's "See the fans who won this" action: switch back to
  // the leaderboard sub-view and apply the slug/type filter — the dropdown then shows the
  // selected prize because `filters.slug` (fed to LoyaltyToolbar) is part of the same
  // `queryState` this sets. `view` and `queryState` are separate state variables feeding
  // separate effects (the ranking fetch effect only depends on [isAuth, queryState]), so
  // setting them in two calls here does not risk the double-fetch the single-state-object
  // convention at line ~82 guards against — that convention is about filters/page/sorting,
  // which handleFilterChange already updates atomically in one setState call.
  const handleSelectPrizeWinners = (slugName) => {
    setView('leaderboard');
    handleFilterChange({ slug: slugName, type: 'prize' });
  };

  const handleRowClick = (email, rank) => {
    setSelectedRank(rank);
    setSelectedEmail(email);
    setDetail(null);
    setDetailLoading(true);
  };

  const handleSelectFan = (email) => {
    setSelectedRank('—');
    setSelectedEmail(email);
    setDetail(null);
    setDetailLoading(true);
  };

  const handleCloseFanModal = () => {
    setSelectedEmail(null);
    setSelectedRank('—');
    setDetail(null);
    setDetailLoading(false);
  };

  const handlePrevPage = () => {
    setQueryState(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }));
  };

  const handleNextPage = () => {
    if (!rankingHasMore) return;
    setQueryState(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const loadMoreActivity = async () => {
    if (activityLoading) return;
    setActivityLoading(true);
    const from = activityItems.length;
    const res = await getLoyaltyActivity({ from, to: from + SECTION_PAGE });
    setActivityItems(prev => [...prev, ...((res && res.items) || [])]);
    setActivityHasMore(!!(res && res.hasMore));
    setActivityLoading(false);
  };

  const handleExport = async () => {
    setDownloadingInProgress(true);
    try {
      const result = await getLoyaltyRanking({
        ...filters,
        from: 0,
        to: EXPORT_MAX_ROWS,
        sortingField: sorting[0],
        sortingType: sorting[1],
        getTotal: false,
      });

      const items = (result && result.isAuth && result.items) || [];
      const data = makeLoyaltyCsv(items);
      const element = document.createElement('a');
      const file = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      element.href = URL.createObjectURL(file);
      element.download = 'loyalty.csv';
      document.body.appendChild(element); // Required for this to work in FireFox
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.log('\x1b[31m%s\x1b[0m', `${new Date().toLocaleTimeString()} ~ Loyalty CSV export error: ${error}`);
    }
    setDownloadingInProgress(false);
  };

  if (isLoading) return <LoadIcon />;

  const slugOptions = buildPrizeSlugOptions(prizes);

  return (
    <div className="loyalty">
      <div className="frame">
        <div className="appbar">
          <div className="crumb">
            Admin /
            {' '}
            <b>Loyalty</b>
          </div>
          <div className="title" style={{ marginLeft: 6 }}>Loyalty</div>
        </div>
        {isAuth ? (
          <>
            <div className="subtabs">
              <button
                type="button"
                className={`subtab${view === 'leaderboard' ? ' on' : ''}`}
                onClick={() => setView('leaderboard')}
              >
                Leaderboard
              </button>
              <button
                type="button"
                className={`subtab${view === 'prizes' ? ' on' : ''}`}
                onClick={() => setView('prizes')}
              >
                Prizes
              </button>
            </div>
            <LoyaltyKpis kpis={dashboard.kpis} />
            {view === 'leaderboard' ? (
              <>
                <div className="panel" style={{ marginBottom: 14 }}>
                  <LoyaltyToolbar
                    filters={filters}
                    onChange={handleFilterChange}
                    slugOptions={slugOptions}
                    onExport={handleExport}
                    downloadingInProgress={downloadingInProgress}
                  />
                  {rankingLoading ? <LoadIcon /> : (
                    <LoyaltyRankingTable
                      items={ranking}
                      page={page}
                      pageSize={PAGE_SIZE}
                      hasMore={rankingHasMore}
                      asOf={rankingAsOf}
                      sorting={sorting}
                      onSort={handleSort}
                      onRowClick={handleRowClick}
                      onPrevPage={handlePrevPage}
                      onNextPage={handleNextPage}
                    />
                  )}
                </div>
                <LoyaltyActivity
                  items={activityItems}
                  onSelectFan={handleSelectFan}
                  hasMore={activityHasMore}
                  loading={activityLoading}
                  onLoadMore={loadMoreActivity}
                />
              </>
            ) : (
              <LoyaltyPrizesView onSelectPrizeWinners={handleSelectPrizeWinners} />
            )}
          </>
        ) : (
          <div>You are not authorized to view the Loyalty dashboard.</div>
        )}
      </div>
      {selectedEmail ? (
        <LoyaltyUserModal
          detail={detail}
          rank={selectedRank}
          loading={detailLoading}
          onClose={handleCloseFanModal}
        />
      ) : null}
    </div>
  );
};

export default LoyaltyContainer;
